/**
 * SearchService - title search on the BKFC CMS API, the same backend search the
 * apps use: GET /v1/search (bkfc-cms src/routes/discovery.ts).
 *
 * All four entity types are requested and grouped in the apps' lane order
 * (event, fighter, media, article), keeping the server's rank within a type:
 *   - media: a link to its watch page, /watch/:slug.
 *   - fighter, event: a link to web's own page, /fighters/:slug or /events/:slug,
 *     which read the same CMS ids directly.
 *   - article: a link out to the bkfc.com page the CMS gives (share_url).
 *
 * Anonymous on purpose (no credentials, no Authorization): the anonymous body is
 * edge-cached, and web holds no CMS session to send. `access` is therefore the
 * anonymous verdict and is not shown; the player gates as it does for every
 * other card.
 */
import { CMS_API_BASE_URL } from '../config/backendEndpoints';
import { APP_CONFIG } from '../config/AppConfig';
import { imageUrl } from '../api/image';

/** The server's own ceiling (SEARCH_MAX_LIMIT); one page is all the grid shows. */
export const SEARCH_LIMIT = 50;
export const CARD_IMAGE_WIDTH = 640;

export function searchHitToChannel(hit) {
  return {
    _id: hit.id,
    slug: hit.slug,
    title: hit.title?.value || '',
    thumbnail: imageUrl(hit.art?.landscape || hit.art?.portrait, '16x9', CARD_IMAGE_WIDTH),
    geo_ok: hit.geo_ok,
    starts_at: hit.starts_at || undefined,
    watch_on: hit.watch_on || null,
    entityType: 'media',
    link: { to: `/watch/${hit.slug || hit.id}` },
  };
}

/** The apps' lane order (bkfc-mobile groupHits.ts ENTITY_TYPE_ORDER). */
export const ENTITY_TYPE_ORDER = ['event', 'fighter', 'media', 'article'];
const LABELS = { event: 'Event', fighter: 'Fighter', article: 'News' };

function linkCard(hit, link) {
  return {
    _id: hit.id,
    slug: hit.slug,
    title: hit.title?.value || '',
    thumbnail: imageUrl(hit.art?.landscape || hit.art?.portrait, '16x9', CARD_IMAGE_WIDTH),
    geo_ok: hit.geo_ok,
    watch_on: hit.watch_on || null,
    entityType: hit.entity_type,
    label: LABELS[hit.entity_type],
    link,
  };
}

function hitToCard(hit) {
  switch (hit.entity_type) {
    case 'media':
      // An off-app broadcast (`watch_on` set) is listed with its partner; its page links out.
      return searchHitToChannel(hit);
    case 'fighter':
      return linkCard(hit, { to: `/fighters/${hit.slug || hit.id}` });
    case 'event':
      return linkCard(hit, { to: `/events/${hit.slug || hit.id}` });
    case 'article':
      return hit.share_url ? linkCard(hit, { href: hit.share_url }) : null;
    default:
      return null;
  }
}

export async function mapSearchResponse(body) {
  const hits = Array.isArray(body?.data) ? body.data : [];
  const cards = hits.map(hitToCard).filter(Boolean);
  // Array#sort is stable: the server's rank order holds within each type.
  cards.sort(
    (a, b) => ENTITY_TYPE_ORDER.indexOf(a.entityType) - ENTITY_TYPE_ORDER.indexOf(b.entityType),
  );
  return { channels: cards, total: cards.length };
}

class SearchService {
  constructor() {
    // Settled results per query: one successful request per query string for the session.
    this.cache = new Map();
  }

  /** `entityTypes` narrows the search, e.g. 'media' for a fighter's titles. */
  async searchChannels(query, { signal, entityTypes } = {}) {
    const q = (query || '').trim();
    if (q.length < 2) {
      // The server refuses q shorter than 2 characters.
      return { channels: [], total: 0 };
    }
    if (!CMS_API_BASE_URL) {
      throw new Error('Search is not configured: REACT_APP_CMS_API_URL is not set.');
    }

    const key = `${entityTypes || 'all'}:${q.toLowerCase()}`;
    if (this.cache.has(key)) {
      return this.cache.get(key);
    }

    const params = new URLSearchParams({
      q,
      limit: String(SEARCH_LIMIT),
      platform: 'web',
    });
    if (entityTypes) {
      params.set('entity_types', entityTypes);
    }
    // Each caller gets its own request: sharing an in-flight one would hand a
    // caller a request another caller's AbortController can cancel.
    const response = await fetch(`${CMS_API_BASE_URL}/v1/search?${params}`, {
      headers: {
        Accept: 'application/json',
        // Required by the API (semver); the same build version the app reports elsewhere.
        'X-BKFC-App-Version': APP_CONFIG.APP_VERSION,
      },
      signal,
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const error = new Error(body?.error?.message || `GET /v1/search failed (${response.status})`);
      error.status = response.status;
      throw error;
    }
    const result = await mapSearchResponse(await response.json());
    this.cache.set(key, result);
    return result;
  }
}

export default new SearchService();
